import React from "react";
import { Loader2, Inbox } from "lucide-react";
import { cn } from "@/lib/utils";

export function PageHeader({ title, subtitle, children, testid }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3 mb-6" data-testid={testid}>
      <div>
        <h1 className="text-3xl sm:text-4xl font-bold uppercase tracking-tight text-foreground">{title}</h1>
        {subtitle && <p className="text-sm text-muted-foreground mt-1">{subtitle}</p>}
      </div>
      {children && <div className="flex items-center gap-2 flex-wrap">{children}</div>}
    </div>
  );
}

const STATUS_COLORS = {
  running: "bg-success/15 text-success border-success/30",
  completed: "bg-success/15 text-success border-success/30",
  "in production": "bg-primary/15 text-primary border-primary/30",
  "in development": "bg-primary/15 text-primary border-primary/30",
  idle: "bg-warning/15 text-warning border-warning/30",
  setup: "bg-warning/15 text-warning border-warning/30",
  "on hold": "bg-warning/15 text-warning border-warning/30",
  delayed: "bg-destructive/15 text-destructive border-destructive/30",
  breakdown: "bg-destructive/15 text-destructive border-destructive/30",
  rejected: "bg-destructive/15 text-destructive border-destructive/30",
  "not started": "bg-muted text-muted-foreground border-border",
  maintenance: "bg-primary/15 text-primary border-primary/30",
  offline: "bg-muted text-muted-foreground border-border",
  approved: "bg-success/15 text-success border-success/30",
  "final approved": "bg-success/15 text-success border-success/30",
  "revision required": "bg-warning/15 text-warning border-warning/30",
  dispatched: "bg-purple-500/15 text-purple-400 border-purple-500/30",
  "ready to dispatch": "bg-primary/15 text-primary border-primary/30",
};

export function StatusBadge({ status, testid }) {
  const key = (status || "").toLowerCase();
  const cls = STATUS_COLORS[key] || "bg-secondary text-secondary-foreground border-border";
  return (
    <span data-testid={testid} className={cn("inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold border whitespace-nowrap", cls)}>
      <span className="w-1.5 h-1.5 rounded-full bg-current" />
      {status || "—"}
    </span>
  );
}

export function Loading({ label = "Loading" }) {
  return (
    <div className="flex items-center justify-center py-20 text-muted-foreground" data-testid="loading-state">
      <Loader2 className="w-5 h-5 animate-spin mr-2" /> {label}…
    </div>
  );
}

export function EmptyState({ title = "Nothing here yet", hint, action, testid }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center" data-testid={testid || "empty-state"}>
      <div className="w-14 h-14 rounded-2xl bg-secondary flex items-center justify-center mb-4">
        <Inbox className="w-7 h-7 text-muted-foreground" />
      </div>
      <p className="font-semibold text-foreground">{title}</p>
      {hint && <p className="text-sm text-muted-foreground mt-1 max-w-sm">{hint}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

export function ProgressBar({ value, testid }) {
  const v = Math.min(Math.max(value || 0, 0), 100);
  const color = v >= 100 ? "bg-success" : v >= 60 ? "bg-primary" : "bg-warning";
  return (
    <div className="w-full h-2 rounded-full bg-secondary overflow-hidden" data-testid={testid}>
      <div className={cn("h-full rounded-full transition-all duration-500", color)} style={{ width: `${v}%` }} />
    </div>
  );
}

export function fmt(n) {
  if (n === null || n === undefined || isNaN(n)) return "0";
  return Number(n).toLocaleString();
}
